<template>
  <b-modal v-model="active" size="xl" :id="modalId" scrollable>
    <div slot="modal-header" minimize class="app-modal-header">
      <span class="app-modal-header__icon"><AppIcon name="box" /></span>
      <h5 class="app-modal-header__title">Asiento de inventario</h5>
      <AccountingBookBadge :declared="declared" class="ml-2" />
    </div>

    <p class="small text-muted mb-3">
      Valoriza el stock a una fecha (cantidad × costo de compra), lo compara con el saldo de Mercaderías y pasa la diferencia a costo de mercadería vendida.
      Hacelo al terminar cada mes o antes del cierre del ejercicio.
      <span v-if="last">Último: <b>{{ formatDate(last.date) }}</b> · {{ formatCurrency(last.stock_value) }}.</span>
    </p>

    <div class="inventory-controls">
      <div>
        <label class="filter-label">Fecha del inventario</label>
        <b-form-input type="date" size="sm" v-model="date" :max="today" @change="data = null"></b-form-input>
      </div>
      <b-button variant="dark" size="sm" :disabled="loading || !date" @click="preview()">
        <b-spinner small class="mr-1" v-if="loading" /><AppIcon name="calculator" class="mr-1" v-else />Calcular
      </b-button>
    </div>

    <div v-if="data" class="mt-3">
      <b-alert show variant="danger" class="mb-2" v-for="issue in data.issues" :key="'i' + issue">{{ issue }}</b-alert>
      <b-alert show variant="warning" class="mb-2" v-for="warning in data.warnings" :key="'w' + warning">{{ warning }}</b-alert>

      <div class="inventory-kpis">
        <div class="inventory-kpi">
          <small class="inventory-kpi__label">Stock valorizado al {{ formatDate(data.date) }}</small>
          <b-input-group size="sm" prepend="$" class="inventory-kpi__input">
            <b-form-input type="number" step="0.01" min="0" v-model="value" @change="preview(true)"></b-form-input>
          </b-input-group>
          <small class="text-muted" v-if="data.edited">
            Corregido. El sistema calculó {{ formatCurrency(data.calculated_value) }} ·
            <b-link @click="resetValue()">usar el calculado</b-link>
          </small>
          <small class="text-muted" v-else>{{ data.products_count }} producto/s con stock. Podés corregirlo.</small>
        </div>
        <div class="inventory-kpi__op">−</div>
        <div class="inventory-kpi">
          <small class="inventory-kpi__label">Saldo de Mercaderías</small>
          <div class="inventory-kpi__value">{{ formatCurrency(data.account_balance) }}</div>
          <small class="text-muted" v-if="data.inventory_account">{{ data.inventory_account.code }} {{ data.inventory_account.name }}</small>
        </div>
        <div class="inventory-kpi__op">=</div>
        <div class="inventory-kpi" :class="data.difference < 0 ? 'inventory-kpi--out' : 'inventory-kpi--in'">
          <small class="inventory-kpi__label">Diferencia</small>
          <div class="inventory-kpi__value">{{ formatCurrency(data.difference) }}</div>
          <small v-if="data.difference < 0">Salió mercadería: va a costo de ventas</small>
          <small v-else-if="data.difference > 0">Hay más stock que lo registrado: baja el costo de ventas</small>
          <small v-else class="text-muted">Sin diferencia</small>
        </div>
      </div>

      <b-table v-if="data.lines.length"
               class="app-table app-table--compact mt-3 mb-0"
               :items="data.lines"
               :fields="lineFields"
               :small="true">
        <template v-slot:cell(account)="row">
          <span :class="{ 'pl-4': row.item.type == 'Haber' }">{{ row.item.accounting_accounts_code }} · {{ row.item.accounting_accounts_name }}</span>
        </template>
        <template v-slot:cell(debit)="row"><b v-if="row.item.type == 'Debe'">{{ formatCurrency(row.item.total) }}</b></template>
        <template v-slot:cell(credit)="row"><b v-if="row.item.type == 'Haber'">{{ formatCurrency(row.item.total) }}</b></template>
      </b-table>

      <b-button variant="link" size="sm" class="px-0 mt-2" v-if="data.items.length" @click="showItems = !showItems">
        <AppIcon :name="showItems ? 'chevron-up' : 'chevron-down'" class="mr-1" />{{ showItems ? 'Ocultar' : 'Ver' }} detalle por producto ({{ data.items.length }})
      </b-button>
      <div v-if="showItems">
        <b-form-input size="sm" class="mb-2" v-model="search" placeholder="Buscar producto por código o nombre"></b-form-input>
        <b-table class="app-table app-table--compact mb-0"
                 :items="filteredItems"
                 :fields="itemFields"
                 :small="true"
                 sticky-header="320px"
                 :tbody-tr-class="itemRowClass">
          <template v-slot:cell(quantity)="row">{{ formatNumber(row.item.quantity) }}</template>
          <template v-slot:cell(cost)="row">
            <span v-if="row.item.cost !== null">{{ formatCurrency(row.item.cost) }}</span>
            <span v-else class="text-danger">Sin costo</span>
          </template>
          <template v-slot:cell(total)="row"><b>{{ formatCurrency(row.item.total) }}</b></template>
        </b-table>
      </div>

      <b-row class="mt-3">
        <b-col md="4">
          <b-form-group label="Referencia" class="mb-0">
            <b-form-input type="text" size="sm" v-model="reference"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="Observaciones" class="mb-0">
            <b-form-input type="text" size="sm" v-model="observations"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <div slot="modal-footer">
      <b-button variant="outline-secondary" size="sm" class="mr-1" @click="active = false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
      <b-button variant="dark" size="sm" :disabled="loading || !data || !data.ready || !reference.trim()" @click="generate()"><AppIcon name="save" class="mr-1" />Generar asiento</b-button>
    </div>
  </b-modal>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import moment from 'moment'
  import AccountingBookBadge from './AccountingBookBadge'

  // Asiento de inventario de un libro: calcula la diferencia de stock y la genera.
  export default {
    components: {
      AccountingBookBadge,
    },
    props: {
      businessId: {
        default: null,
      },
      declared: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        active: false,
        modalId: 'inventory_' + Math.random().toString(36).slice(2, 8),
        today: moment().format('YYYY-MM-DD'),
        date: '',
        value: null,
        data: null,
        last: null,
        loading: false,
        showItems: false,
        search: '',
        reference: '',
        observations: 'Ajuste de inventario de mercaderías',
        lineFields: [
          { key: 'account', label: 'Cuenta', class: 'align-middle' },
          { key: 'debit',   label: 'Debe',   class: 'align-middle text-right' },
          { key: 'credit',  label: 'Haber',  class: 'align-middle text-right' },
        ],
        itemFields: [
          { key: 'code',     label: 'Código',   class: 'align-middle' },
          { key: 'name',     label: 'Producto', class: 'align-middle' },
          { key: 'quantity', label: 'Stock',    class: 'align-middle text-right' },
          { key: 'cost',     label: 'Costo',    class: 'align-middle text-right' },
          { key: 'total',    label: 'Valor',    class: 'align-middle text-right' },
        ],
      }
    },
    computed: {
      filteredItems() {
        const items = this.data ? this.data.items : []
        const search = this.search.trim().toLowerCase()
        if(!search) return items
        return items.filter(item => (item.code || '').toLowerCase().includes(search) || (item.name || '').toLowerCase().includes(search))
      },
    },
    methods: {
      // last: último inventario del libro, para mostrarlo como referencia
      open(last = null) {
        this.last = last
        this.date = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        this.value = null
        this.data = null
        this.showItems = false
        this.search = ''
        this.observations = 'Ajuste de inventario de mercaderías'
        this.active = true
        this.preview()
      },
      // edited: el contador corrigió el importe; si no, se usa el calculado
      preview(edited = false) {
        if(!this.date) return
        this.loading = true
        serviceAPI.previewInventario({
          business_id: this.businessId,
          declared:    this.declared,
          date:        this.date,
          stock_value: edited && this.value !== '' ? this.value : null,
        })
        .then(response => {
          this.data = response.data
          this.value = response.data.stock_value
          this.reference = 'INVENTARIO-' + moment(this.date).format('YYYY-MM')
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      resetValue() {
        this.value = null
        this.preview()
      },
      generate() {
        this.loading = true
        serviceAPI.generarInventario({
          business_id:  this.businessId,
          declared:     this.declared,
          date:         this.date,
          stock_value:  this.data.edited ? this.value : null,
          reference:    this.reference,
          observations: this.observations,
        })
        .then(response => {
          this.$awn.success('Asiento de inventario generado')
          this.active = false
          this.$emit('saved', response.data)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
        .finally(() => {
          this.loading = false
        })
      },
      itemRowClass(item) {
        if(!item) return ''
        return item.quantity < 0 || item.cost === null ? 'table-danger' : ''
      },
      formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      formatNumber(value) {
        return Intl.NumberFormat('es-AR', { maximumFractionDigits: 4 }).format(value || 0)
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0)
      },
    },
  }
</script>
<style scoped>
  .inventory-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--app-color-text-secondary);
    display: block;
  }
  .inventory-kpis {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
  }
  .inventory-kpi {
    flex: 1 1 200px;
    padding: 10px 14px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }
  .inventory-kpi__label {
    display: block;
    font-weight: 600;
    color: var(--app-color-text-secondary);
    margin-bottom: 4px;
  }
  .inventory-kpi__value {
    font-size: 18px;
    font-weight: 700;
  }
  .inventory-kpi__input {
    max-width: 220px;
    margin-bottom: 2px;
  }
  .inventory-kpi__op {
    align-self: center;
    font-size: 20px;
    font-weight: 700;
    color: var(--app-color-text-secondary);
  }
  .inventory-kpi--out {
    border-left: 4px solid var(--app-color-danger);
  }
  .inventory-kpi--in {
    border-left: 4px solid var(--app-color-success);
  }
</style>
